import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Modal, Form, Input, Select, message, Row, Col,
  Alert, Avatar, Descriptions, Tag, Badge, Divider,
  Tooltip, Popconfirm,
  Button
} from 'antd';
import {
  UserOutlined, PhoneOutlined, HomeOutlined,
  MailOutlined, CalendarOutlined, TeamOutlined,
  EnvironmentOutlined, EyeOutlined, EditOutlined,
  DeleteOutlined, PlusOutlined, SearchOutlined,
  InfoCircleOutlined, CheckCircleOutlined,
  DollarOutlined, ShoppingOutlined, FileTextOutlined,
  TruckOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../utils/axios';
import ReusableTable from '../../components/ReusableTable';

const { Option } = Select;
const { TextArea } = Input;

const ClientPage = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({
    search: ''
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
 
  const [paginationInfo, setPaginationInfo] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });

  // Fonction pour charger les clients
  const fetchClients = async (page = 1, limit = 10, search = '') => {
    setLoading(true);
    try {
      const params = { page, limit, search: search || undefined };
     
      const response = await api.get('/api/clients', { params });
     
      if (response.data.success) {
        const { clients: clientsData, pagination } = response.data.data;
       
        const formattedClients = clientsData.map(client => ({
          ...client,
          id: client.id || client._id,
          prenom: client.prenom || '',
          nom: client.nom || '',
          telephone: client.telephone || '',
          telephoneFormatted: client.telephoneFormatted || formatTelephone(client.telephone),
          adresse: client.adresse || '',
          dateCreation: client.dateCreation || client.createdAt,
          dateCreationFormatted: client.dateCreationFormatted ||
            dayjs(client.dateCreation || client.createdAt).format('DD/MM/YYYY HH:mm'),
          totalVersements: client.totalVersements || 0,
          totalRestant: client.totalRestant || 0,
          totalVentes: client.totalVentes || 0,
          totalTonnes: client.totalTonnes || 0,
          totalFactures: client.totalFactures || 0,
          statut: client.soldeRestant > 0 ? 'En attente' : 'À jour'
        }));
       
        setClients(formattedClients);
        setFilteredClients(formattedClients);
        setPaginationInfo({
          currentPage: pagination.currentPage,
          totalPages: pagination.totalPages,
          totalItems: pagination.totalItems,
          itemsPerPage: pagination.itemsPerPage
        });
       
        if (formattedClients.length === 0) {
          message.info('Aucun client trouvé');
        }
      } else {
        message.error(response.data.message || 'Erreur lors du chargement');
        setClients([]);
        setFilteredClients([]);
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Erreur de connexion au serveur');
      setClients([]);
      setFilteredClients([]);
    } finally {
      setLoading(false);
    }
  };

  // Appliquer les filtres
  const applyFilters = () => {
    let filtered = [...clients];
   
    if (filters.search) {
      filtered = filtered.filter(client =>
        (client.prenom?.toLowerCase() || '').includes(filters.search.toLowerCase()) ||
        (client.nom?.toLowerCase() || '').includes(filters.search.toLowerCase()) ||
        (client.telephone || '').includes(filters.search) ||
        (client.adresse?.toLowerCase() || '').includes(filters.search.toLowerCase())
      );
    }
   
    setFilteredClients(filtered);
    setCurrentPage(1);
  };

  // Gestion du changement de filtre
  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
   
    if (field === 'search') {
      fetchClients(1, pageSize, value);
    }
  };

  // Initialiser les clients
  useEffect(() => {
    fetchClients(1, pageSize);
  }, []);

  // Appliquer les filtres locaux
  useEffect(() => {
    if (filters.search && !loading) {
      applyFilters();
    }
  }, [clients, filters.search]);

  // Ouvrir modal pour ajouter/modifier
  const showModal = (client = null) => {
    setEditingClient(client);
    setErrorMessage('');
   
    if (client) {
      form.setFieldsValue({
        prenom: client.prenom,
        nom: client.nom,
        telephone: client.telephone,
        adresse: client.adresse,
        email: client.email || ''
      });
    } else {
      form.resetFields();
    }
    setIsModalVisible(true);
  };

  // Ouvrir modal de détail
  const showDetailModal = (client) => {
    setSelectedClient(client);
    setIsDetailModalVisible(true);
  };

  // Navigation vers la page de détail
  const navigateToDetail = (clientId) => {
    navigate(`/clients/${clientId}`);
  };

  // Fermer modals
  const handleCancel = () => {
    setIsModalVisible(false);
    setIsDetailModalVisible(false);
    setEditingClient(null);
    setSelectedClient(null);
    setErrorMessage('');
    form.resetFields();
  };

  // Validation du téléphone
  const validateTelephone = (_, value) => {
    if (!value) {
      return Promise.reject(new Error('Veuillez saisir le numéro de téléphone'));
    }
   
    const phoneRegex = /^(\+221|00221|221)?(33|70|76|77|78)\d{7}$/;
    if (!phoneRegex.test(value.replace(/\s/g, ''))) {
      return Promise.reject(new Error('Numéro de téléphone invalide (ex: 771234567, +221 33 821 45 67)'));
    }
   
    return Promise.resolve();
  };

  // Validation de l'email
  const validateEmail = (_, value) => {
    if (!value) return Promise.resolve();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      return Promise.reject(new Error('Format email invalide'));
    }
    return Promise.resolve();
  };

  // Soumettre le formulaire
  const handleSubmit = async (values) => {
    setSubmitting(true);
    setErrorMessage('');
   
    try {
      if (editingClient) {
        const response = await api.put(`/api/clients/${editingClient.id}`, values);
       
        if (response.data.success) {
          message.success('Client modifié avec succès !');
          fetchClients(currentPage, pageSize, filters.search);
          handleCancel();
        } else {
          throw new Error(response.data.message);
        }
      } else {
        const response = await api.post('/api/clients', values);
       
        if (response.data.success) {
          message.success('Client ajouté avec succès !');
          fetchClients(currentPage, pageSize, filters.search);
          handleCancel();
        } else {
          throw new Error(response.data.message);
        }
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Erreur lors de la sauvegarde';
      const errors = error.response?.data?.errors;
     
      if (errors && Array.isArray(errors)) {
        setErrorMessage(
          <div>
            <div>{errorMessage}</div>
            <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
              {errors.map((err, idx) => (
                <li key={idx} style={{ fontSize: '12px' }}>{err}</li>
              ))}
            </ul>
          </div>
        );
      } else {
        setErrorMessage(errorMessage);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Supprimer un client
  const handleDelete = async (client) => {
    try {
      const response = await api.delete(`/api/clients/${client.id}`);
     
      if (response.data.success) {
        message.success('Client supprimé avec succès !');
        fetchClients(currentPage, pageSize, filters.search);
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      message.error(error.response?.data?.message || error.message || 'Erreur lors de la suppression');
    }
  };

  // Format du téléphone pour l'affichage
  const formatTelephone = (telephone) => {
    if (!telephone) return '';
   
    const cleaned = telephone.replace(/\D/g, '');
   
    if (cleaned.length === 12 && cleaned.startsWith('221')) {
      const part1 = cleaned.slice(0, 3);
      const part2 = cleaned.slice(3, 5);
      const part3 = cleaned.slice(5, 8);
      const part4 = cleaned.slice(8, 10);
      const part5 = cleaned.slice(10, 12);
      return `+${part1} ${part2} ${part3} ${part4} ${part5}`;
    } else if (cleaned.length === 9) {
      const part1 = cleaned.slice(0, 2);
      const part2 = cleaned.slice(2, 5);
      const part3 = cleaned.slice(5, 7);
      const part4 = cleaned.slice(7, 9);
      return `+221 ${part1} ${part2} ${part3} ${part4}`;
    }
   
    return telephone;
  };

  // Options de statut
  const statusOptions = [
    {
      value: 'À jour',
      label: 'À jour',
      color: '#52c41a',
      backgroundColor: '#f6ffed',
      borderColor: '#b7eb8f',
      icon: <CheckCircleOutlined />
    },
    {
      value: 'En attente',
      label: 'En attente',
      color: '#fa8c16',
      backgroundColor: '#fff7e6',
      borderColor: '#ffd591',
      icon: <InfoCircleOutlined />
    }
  ];

  // Calculer les statistiques
  const stats = {
    total: filteredClients.length,
    'à jour': filteredClients.filter(c => c.statut === 'À jour').length,
    'en attente': filteredClients.filter(c => c.statut === 'En attente').length,
    ventes: filteredClients.reduce((sum, c) => sum + (c.totalVentes || 0), 0),
    tonnes: filteredClients.reduce((sum, c) => sum + (c.totalTonnes || 0), 0)
  };

  // Données paginées
  const paginatedData = filteredClients.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Configuration des colonnes
  const columns = [
    {
      title: 'Prénom',
      dataIndex: 'prenom',
      key: 'prenom',
      width: 120,
      render: (text) => <span style={{ fontWeight: '500' }}>{text || '-'}</span>,
      cellStyle: { fontWeight: '500' }
    },
    {
      title: 'Nom',
      dataIndex: 'nom',
      key: 'nom',
      width: 120,
      render: (text) => <span style={{ fontWeight: '600' }}>{text || '-'}</span>,
      cellStyle: { fontWeight: '600' }
    },
    {
      title: 'Téléphone',
      dataIndex: 'telephone',
      key: 'telephone',
      width: 150,
      render: (telephone, record) => {
        const formatted = formatTelephone(telephone);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <PhoneOutlined style={{ color: '#1890ff', fontSize: '14px' }} />
            <span style={{
              fontFamily: 'monospace',
              fontSize: '13px',
              fontWeight: '600',
              color: '#1890ff'
            }}>
              {formatted || 'Non renseigné'}
            </span>
          </div>
        );
      }
    },
    {
      title: 'Adresse',
      dataIndex: 'adresse',
      key: 'adresse',
      width: 180,
      render: (adresse) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <EnvironmentOutlined style={{ color: '#722ed1', fontSize: '14px' }} />
          <span style={{
            fontSize: '12px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }} title={adresse}>
            {adresse || 'Non renseignée'}
          </span>
        </div>
      )
    },
    {
      title: 'Statut',
      dataIndex: 'statut',
      key: 'statut',
      width: 100,
      render: (statut) => {
        const statusOption = statusOptions.find(s => s.value === statut) || statusOptions[0];
        return (
          <span style={{
            display: 'inline-block',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: '500',
            backgroundColor: statusOption.backgroundColor,
            color: statusOption.color,
            border: `1px solid ${statusOption.borderColor}`,
            minWidth: '80px',
            textAlign: 'center'
          }}>
            {statut}
          </span>
        );
      },
      cellStyle: { textAlign: 'center' }
    }
  ];

  // Configuration des filtres
  const tableFilters = [
    {
      field: 'statut',
      placeholder: 'Statut',
      value: '',
      options: [
        { value: 'À jour', label: 'À jour' },
        { value: 'En attente', label: 'En attente' }
      ],
      width: '120px'
    }
  ];

  return (
    <>
      <ReusableTable
        // Configuration de base
        title="Gestion des Clients"
        data={paginatedData}
        columns={columns}
        loading={loading}
       
        // Pagination
        totalItems={filteredClients.length}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={(page, size) => {
          setCurrentPage(page);
          if (size) setPageSize(size);
        }}
        onSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
       
        // Recherche et filtres
        searchText={filters.search}
        onSearchChange={(value) => handleFilterChange('search', value)}
        onSearch={() => fetchClients(1, pageSize, filters.search)}
        filters={tableFilters}
        onFilterChange={handleFilterChange}
       
        // Actions
        onAdd={() => showModal()}
        onRefresh={() => fetchClients(currentPage, pageSize, filters.search)}
        onEdit={showModal}
        onDelete={handleDelete}
        onView={showDetailModal}
       
        // Personnalisation
        addButtonText="Nouveau Client"
        searchPlaceholder="Rechercher client..."
        showStats={true}
        stats={stats}
       
        // Colonnes spéciales
        showStatusColumn={false} // On utilise notre propre colonne statut
        statusOptions={statusOptions}
       
        // Configuration des boutons d'action
        actionButtons={['view', 'edit', 'delete']}
       
        // Personnalisation des couleurs
        primaryColor="#5fa5e7"
        borderColor="#5fa5e7"
       
        // Options supplémentaires
        extraButtons={
          <Button
            type="default"
            onClick={() => {
              setFilters({ search: '' });
              fetchClients(1, pageSize, '');
              message.info('Filtres réinitialisés');
            }}
            style={{
              borderColor: '#ff4d4f',
              color: '#ff4d4f'
            }}
          >
            Réinitialiser
          </Button>
        }
       
        // Contenu personnalisé
        headerContent={
          <div style={{ marginBottom: '20px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
              paddingBottom: '16px',
              borderBottom: '1px solid #e8e8e8'
            }}>
              <div>
                <h1 style={{
                  margin: '0 0 8px 0',
                  fontSize: '24px',
                  fontWeight: '600',
                  color: '#333',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <TeamOutlined style={{ color: '#5fa5e7' }} />
                  Gestion des Clients
                </h1>
                <div style={{
                  fontSize: '14px',
                  color: '#666',
                  display: 'flex',
                  gap: '24px',
                  alignItems: 'center'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    <span><strong>{stats['à jour']}</strong> À jour</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <InfoCircleOutlined style={{ color: '#fa8c16' }} />
                    <span><strong>{stats['en attente']}</strong> En attente</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <DollarOutlined style={{ color: '#722ed1' }} />
                    <span><strong>{stats.ventes}</strong> Ventes</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <TruckOutlined style={{ color: '#fa8c16' }} />
                    <span><strong>{stats.tonnes.toFixed(1)}</strong> T</span>
                  </div>
                  <div style={{
                    backgroundColor: '#5fa5e7',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontWeight: '500'
                  }}>
                    Total: {stats.total}
                  </div>
                </div>
              </div>
             
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => showModal()}
                size="large"
                style={{
                  backgroundColor: '#5fa5e7',
                  borderColor: '#5fa5e7',
                  padding: '0 24px',
                  height: '40px',
                  fontWeight: '500',
                  borderRadius: '6px',
                  boxShadow: '0 2px 8px rgba(95, 165, 231, 0.3)'
                }}
              >
                Nouveau Client
              </Button>
            </div>
          </div>
        }
      />

      {/* Modal pour ajouter/modifier un client */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {editingClient ? (
              <>
                <EditOutlined style={{ color: '#5fa5e7', fontSize: '20px' }} />
                <span style={{ fontSize: '18px', fontWeight: '600' }}>Modifier le Client</span>
              </>
            ) : (
              <>
                <PlusOutlined style={{ color: '#5fa5e7', fontSize: '20px' }} />
                <span style={{ fontSize: '18px', fontWeight: '600' }}>Nouveau Client</span>
              </>
            )}
          </div>
        }
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={700}
        style={{ top: 20 }}
        bodyStyle={{ padding: 0 }}
        destroyOnClose
      >
        {errorMessage && (
          <Alert
            message="Erreur"
            description={errorMessage}
            type="error"
            showIcon
            style={{
              margin: '20px 20px 0 20px',
              borderRadius: '6px'
            }}
            closable
            onClose={() => setErrorMessage('')}
          />
        )}
     
        <div style={{ padding: '24px' }}>
          <div style={{
            marginBottom: '24px',
            paddingBottom: '16px',
            borderBottom: '1px solid #e8e8e8'
          }}>
            <h2 style={{
              margin: '0',
              fontSize: '16px',
              fontWeight: '600',
              color: '#333'
            }}>
              Informations personnelles
            </h2>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            style={{ marginTop: '8px' }}
          >
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="prenom"
                  label="Prénom"
                  rules={[
                    { required: true, message: 'Veuillez saisir le prénom' },
                    { min: 2, message: 'Minimum 2 caractères' },
                    { max: 50, message: 'Maximum 50 caractères' },
                    { whitespace: true, message: 'Le prénom ne peut pas être vide' }
                  ]}
                >
                  <Input
                    placeholder="Ex: Issa"
                    prefix={<UserOutlined style={{ color: '#999' }} />}
                    size="large"
                    allowClear
                    style={{ borderRadius: '6px' }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="nom"
                  label="Nom"
                  rules={[
                    { required: true, message: 'Veuillez saisir le nom' },
                    { min: 2, message: 'Minimum 2 caractères' },
                    { max: 50, message: 'Maximum 50 caractères' },
                    { whitespace: true, message: 'Le nom ne peut pas être vide' }
                  ]}
                >
                  <Input
                    placeholder="Ex: Konaté"
                    size="large"
                    allowClear
                    style={{ borderRadius: '6px' }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="telephone"
                  label="Téléphone"
                  rules={[{
                    required: true,
                    validator: validateTelephone
                  }]}
                  help={
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      <PhoneOutlined /> Format: 771234567 ou +221 33 821 45 67
                    </div>
                  }
                >
                  <Input
                    placeholder="Ex: 771234567"
                    prefix={<PhoneOutlined style={{ color: '#999' }} />}
                    size="large"
                    allowClear
                    style={{ borderRadius: '6px' }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="email"
                  label="Email (optionnel)"
                  rules={[{ validator: validateEmail }]}
                >
                  <Input
                    placeholder="exemple@email.com"
                    prefix={<MailOutlined style={{ color: '#999' }} />}
                    size="large"
                    allowClear
                    style={{ borderRadius: '6px' }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <div style={{
              margin: '24px 0',
              paddingBottom: '16px',
              borderBottom: '1px solid #e8e8e8'
            }}>
              <h2 style={{
                margin: '0',
                fontSize: '16px',
                fontWeight: '600',
                color: '#333'
              }}>
                Adresse
              </h2>
            </div>

            <Form.Item
              name="adresse"
              label="Adresse complète"
              rules={[
                { required: true, message: 'Veuillez saisir l\'adresse' },
                { min: 5, message: 'Minimum 5 caractères' },
                { max: 200, message: 'Maximum 200 caractères' },
                { whitespace: true, message: 'L\'adresse ne peut pas être vide' }
              ]}
              help={
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  <EnvironmentOutlined /> Ex: Dakar, Plateau, Immeuble Alpha, 2ème étage
                </div>
              }
            >
              <TextArea
                placeholder="Ex: Dakar, Plateau, Immeuble Alpha, 2ème étage"
                rows={3}
                size="large"
                style={{ borderRadius: '6px' }}
              />
            </Form.Item>

            <Divider style={{ margin: '24px 0' }} />

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              marginTop: '32px',
              paddingTop: '20px',
              borderTop: '1px solid #e8e8e8'
            }}>
              <Button
                onClick={handleCancel}
                disabled={submitting}
                size="large"
                style={{
                  padding: '8px 32px',
                  fontWeight: '500',
                  borderRadius: '6px'
                }}
              >
                Annuler
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                disabled={submitting}
                size="large"
                style={{
                  backgroundColor: '#5fa5e7',
                  borderColor: '#5fa5e7',
                  padding: '8px 32px',
                  fontWeight: '500',
                  borderRadius: '6px'
                }}
              >
                {editingClient ? 'Modifier' : 'Enregistrer'}
              </Button>
            </div>
          </Form>
        </div>
      </Modal>

      {/* Modal de détail */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <TeamOutlined style={{ color: '#5fa5e7', fontSize: '20px' }} />
            <span style={{ fontSize: '18px', fontWeight: '600' }}>Détails du Client</span>
          </div>
        }
        open={isDetailModalVisible}
        onCancel={handleCancel}
        footer={[
          <Button key="close" onClick={handleCancel} size="large" style={{ padding: '8px 24px' }}>
            Fermer
          </Button>,
          selectedClient && (
            <Button
              key="edit"
              type="primary"
              onClick={() => {
                handleCancel();
                showModal(selectedClient);
              }}
              size="large"
              style={{
                backgroundColor: '#5fa5e7',
                borderColor: '#5fa5e7',
                padding: '8px 24px'
              }}
              icon={<EditOutlined />}
            >
              Modifier
            </Button>
          ),
          selectedClient && (
            <Button
              key="detail"
              type="primary"
              onClick={() => {
                handleCancel();
                navigateToDetail(selectedClient.id);
              }}
              size="large"
              style={{
                backgroundColor: '#52c41a',
                borderColor: '#52c41a',
                padding: '8px 24px'
              }}
              icon={<EyeOutlined />}
            >
              Voir détail complet
            </Button>
          )
        ]}
        width={700}
      >
        {selectedClient && (
          <div style={{ padding: '20px 0' }}>
            {/* Header avec avatar */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '20px',
              padding: '20px',
              backgroundColor: '#f0f7ff',
              borderRadius: '8px',
              marginBottom: '24px'
            }}>
              <Avatar
                size={80}
                icon={<UserOutlined />}
                style={{
                  backgroundColor: '#5fa5e7',
                  fontSize: '32px'
                }}
              />
              <div style={{ flex: 1 }}>
                <h2 style={{
                  margin: '0 0 8px 0',
                  fontSize: '24px',
                  fontWeight: '600',
                  color: '#333'
                }}>
                  {selectedClient.prenom} {selectedClient.nom}
                </h2>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  {(() => {
                    const statutOption = statusOptions.find(s => s.value === selectedClient.statut);
                    return (
                      <Tag
                        color={statutOption?.color}
                        style={{
                          padding: '6px 16px',
                          fontSize: '14px',
                          fontWeight: '500',
                          borderRadius: '20px'
                        }}
                        icon={statutOption?.icon}
                      >
                        {selectedClient.statut}
                      </Tag>
                    );
                  })()}
                  <Tag
                    color="blue"
                    style={{
                      padding: '6px 16px',
                      fontSize: '14px',
                      fontWeight: '500',
                      borderRadius: '20px'
                    }}
                  >
                    ID: {selectedClient.id?.substring(0, 8)}...
                  </Tag>
                </div>
              </div>
            </div>

            {/* Statistiques */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '12px',
              marginBottom: '24px'
            }}>
              <div style={{
                backgroundColor: '#f6ffed',
                padding: '16px',
                borderRadius: '8px',
                textAlign: 'center',
                border: '1px solid #b7eb8f'
              }}>
                <DollarOutlined style={{ fontSize: '24px', color: '#52c41a', marginBottom: '8px' }} />
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#52c41a' }}>
                  {selectedClient.totalVentes || 0}
                </div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>Total Ventes</div>
              </div>
             
              <div style={{
                backgroundColor: '#fff7e6',
                padding: '16px',
                borderRadius: '8px',
                textAlign: 'center',
                border: '1px solid #ffd591'
              }}>
                <ShoppingOutlined style={{ fontSize: '24px', color: '#fa8c16', marginBottom: '8px' }} />
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#fa8c16' }}>
                  {selectedClient.totalTonnes || 0}
                </div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>Tonnes</div>
              </div>
             
              <div style={{
                backgroundColor: '#fff0f6',
                padding: '16px',
                borderRadius: '8px',
                textAlign: 'center',
                border: '1px solid #ffadd2'
              }}>
                <FileTextOutlined style={{ fontSize: '24px', color: '#eb2f96', marginBottom: '8px' }} />
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#eb2f96' }}>
                  {selectedClient.totalFactures || 0}
                </div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>Factures</div>
              </div>
             
              <div style={{
                backgroundColor: '#f0f5ff',
                padding: '16px',
                borderRadius: '8px',
                textAlign: 'center',
                border: '1px solid #adc6ff'
              }}>
                <TruckOutlined style={{ fontSize: '24px', color: '#2f54eb', marginBottom: '8px' }} />
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#2f54eb' }}>
                  {selectedClient.totalVersements || 0}
                </div>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>Versements</div>
              </div>
            </div>

            {/* Informations détaillées */}
            <Descriptions
              column={1}
              bordered
              size="middle"
              labelStyle={{
                fontWeight: '600',
                width: '200px',
                backgroundColor: '#fafafa',
                padding: '12px 16px'
              }}
              contentStyle={{
                padding: '12px 16px',
                backgroundColor: 'white'
              }}
              style={{
                borderRadius: '8px',
                overflow: 'hidden'
              }}
            >
              <Descriptions.Item label={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <PhoneOutlined />
                  Téléphone
                </div>
              }>
                <div style={{
                  backgroundColor: '#e6f7ff',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  fontFamily: 'monospace',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#1890ff'
                }}>
                  {formatTelephone(selectedClient.telephone)}
                </div>
              </Descriptions.Item>
             
              {selectedClient.email && (
                <Descriptions.Item label={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <MailOutlined />
                    Email
                  </div>
                }>
                  <div style={{
                    backgroundColor: '#f6ffed',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#52c41a'
                  }}>
                    {selectedClient.email}
                  </div>
                </Descriptions.Item>
              )}
             
              <Descriptions.Item label={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <EnvironmentOutlined />
                  Adresse
                </div>
              }>
                <div style={{
                  backgroundColor: '#fff7e6',
                  padding: '12px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  lineHeight: '1.6'
                }}>
                  {selectedClient.adresse || 'Non renseignée'}
                </div>
              </Descriptions.Item>
             
              <Descriptions.Item label={
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CalendarOutlined />
                  Date de création
                </div>
              }>
                <div style={{
                  backgroundColor: '#fafafa',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  {selectedClient.dateCreationFormatted}
                </div>
              </Descriptions.Item>
             
              <Descriptions.Item label="Solde restant">
                <div style={{
                  backgroundColor: selectedClient.totalRestant > 0 ? '#fff1f0' : '#f6ffed',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  fontFamily: 'monospace',
                  fontSize: '18px',
                  fontWeight: '700',
                  color: selectedClient.totalRestant > 0 ? '#f5222d' : '#52c41a'
                }}>
                  {selectedClient.totalRestant || 0} FCFA
                </div>
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Modal>
    </>
  );
};

export default ClientPage;